import React, { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  IconButton,
  Tabs,
  Tab,
  Chip,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  Grid,
} from '@mui/material';
import { 
  Close as CloseIcon, 
  Satellite, 
  Streetview, 
  SquareFoot, 
  Home,
} from '@mui/icons-material';
import { apiService } from '../services/api';

declare global {
  interface Window {
    google: any;
  }
}

interface PropertyData {
  id: string;
  address: string;
  lotSize: number;
  lotSizeSqFt: number;
  lotSizeFormatted: string;
  geometry: {
    type: 'Polygon';
    coordinates: number[][][];
  };
  propertyType?: string;
  yearBuilt?: number;
  buildingArea?: number;
  landUse?: string;
}

interface SatelliteViewDialogProps {
  open: boolean;
  onClose: () => void;
  address: string;
  title?: string;
}

const SatelliteViewDialog: React.FC<SatelliteViewDialogProps> = ({
  open,
  onClose,
  address,
  title = "Property Location View"
}) => {
  const [activeTab, setActiveTab] = useState(0); // 0 = Satellite, 1 = Street View
  const [propertyData, setPropertyData] = useState<PropertyData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [regridConfigured, setRegridConfigured] = useState(false);
  const [streetViewOpening, setStreetViewOpening] = useState(false);
  const [googleMapsReady, setGoogleMapsReady] = useState(false);
  const [streetViewEmbedLoading, setStreetViewEmbedLoading] = useState(false);
  const [streetViewEmbedError, setStreetViewEmbedError] = useState<string | null>(null);
  const streetViewContainerRef = useRef<HTMLDivElement>(null);
  const streetViewPanoramaRef = useRef<any>(null);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  /** Geocode address to lat,lng so Street View opens at the property, not user location. */
  const openStreetViewInNewTab = async () => {
    setStreetViewOpening(true);
    try {
      const res = await fetch(
        `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(address)}&format=json&limit=1`,
        { headers: { 'Accept-Language': 'en', 'User-Agent': 'LawnHub/1.0 (property verification)' } }
      );
      const data = await res.json();
      if (Array.isArray(data) && data[0]?.lat != null && data[0]?.lon != null) {
        const lat = data[0].lat;
        const lon = data[0].lon;
        const streetViewUrl = `https://www.google.com/maps/@?api=1&map_action=pano&viewpoint=${lat},${lon}`;
        window.open(streetViewUrl, '_blank');
      } else {
        const fallback = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`;
        window.open(fallback, '_blank');
      }
    } catch {
      const fallback = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`;
      window.open(fallback, '_blank');
    } finally {
      setStreetViewOpening(false);
    }
  };

  // Load Google Maps JS API when dialog opens (for embedded Street View)
  useEffect(() => {
    if (!open) return;
    if (window.google?.maps) {
      setGoogleMapsReady(true);
      return;
    }
    const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
    if (!apiKey) return;
    const existing = document.querySelector('script[src*="maps.googleapis.com"]');
    if (existing) {
      const check = setInterval(() => {
        if (window.google?.maps) {
          clearInterval(check);
          setGoogleMapsReady(true);
        }
      }, 100);
      return () => clearInterval(check);
    }
    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places&loading=async`;
    script.async = true;
    script.defer = true;
    script.onload = () => {
      const check = setInterval(() => {
        if (window.google?.maps) {
          clearInterval(check);
          setGoogleMapsReady(true);
        }
      }, 50);
      setTimeout(() => clearInterval(check), 10000);
    };
    document.head.appendChild(script);
  }, [open]);

  // Embed Street View when tab is active and API is ready
  useEffect(() => {
    if (activeTab !== 1 || !googleMapsReady || !streetViewContainerRef.current || !address) return;
    const container = streetViewContainerRef.current;
    let cancelled = false;
    setStreetViewEmbedError(null);
    setStreetViewEmbedLoading(true);
    const geocoder = new window.google.maps.Geocoder();
    geocoder.geocode({ address }, (results: any[], status: string) => {
      if (cancelled) return;
      if (status !== 'OK' || !results?.[0]) {
        setStreetViewEmbedError('Could not find location.');
        setStreetViewEmbedLoading(false);
        return;
      }
      const loc = results[0].geometry.location;
      const lat = loc.lat();
      const lng = loc.lng();
      const latLng = new window.google.maps.LatLng(lat, lng);
      const svc = new window.google.maps.StreetViewService();
      svc.getPanorama({ location: latLng, radius: 100 }, (data: any, svStatus: string) => {
        if (cancelled) return;
        setStreetViewEmbedLoading(false);
        if (svStatus !== 'OK' || !data?.location?.latLng) {
          setStreetViewEmbedError('No Street View at this address.');
          return;
        }
        const panoLatLng = data.location.latLng;
        const panoLat = typeof panoLatLng.lat === 'function' ? panoLatLng.lat() : panoLatLng.lat;
        const panoLng = typeof panoLatLng.lng === 'function' ? panoLatLng.lng() : panoLatLng.lng;
        // Heading from Street View camera toward the property (so view faces the property)
        const dLon = ((lng - panoLng) * Math.PI) / 180;
        const lat1Rad = (panoLat * Math.PI) / 180;
        const lat2Rad = (lat * Math.PI) / 180;
        const y = Math.sin(dLon) * Math.cos(lat2Rad);
        const x =
          Math.cos(lat1Rad) * Math.sin(lat2Rad) -
          Math.sin(lat1Rad) * Math.cos(lat2Rad) * Math.cos(dLon);
        let heading = (Math.atan2(y, x) * 180) / Math.PI;
        heading = (heading + 360) % 360;
        const panorama = new window.google.maps.StreetViewPanorama(container, {
          position: panoLatLng,
          pov: { heading, pitch: 0 },
          addressControl: false,
          linksControl: true,
          panControl: true,
          enableCloseButton: false,
        });
        if (streetViewPanoramaRef.current) {
          streetViewPanoramaRef.current.setContainer(null);
        }
        streetViewPanoramaRef.current = panorama;
      });
    });
    return () => {
      cancelled = true;
      if (streetViewPanoramaRef.current) {
        try {
          streetViewPanoramaRef.current.setContainer(null);
        } catch {
          // Ignore if container already detached (e.g. tab switched)
        }
        streetViewPanoramaRef.current = null;
      }
    };
  }, [activeTab, googleMapsReady, address]);

  // Check Regrid config when dialog opens
  useEffect(() => {
    if (open && address) {
      checkRegridConfig();
    }
  }, [open, address]);

  // Only fetch property data when Regrid is configured (so we don't show estimated/demo data)
  useEffect(() => {
    if (open && address && regridConfigured) {
      fetchPropertyData();
    }
  }, [open, address, regridConfigured]);

  const checkRegridConfig = async () => {
    try {
      const response = await apiService.get('/property-data/config');
      if (response.success) {
        setRegridConfigured((response.data as any).configured);
      }
    } catch (err) {
      console.error('Failed to check Regrid config:', err);
    }
  };

  const fetchPropertyData = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiService.get(`/property-data/boundary?address=${encodeURIComponent(address)}`);
      if (response.success) {
        setPropertyData(response.data as any);
      } else {
        setError(response.message || 'Failed to fetch property data');
      }
    } catch (err: any) {
      console.error('Failed to fetch property data:', err);
      setError(err.message || 'Failed to fetch property data');
    } finally {
      setLoading(false);
    }
  };


  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      sx={{ '& .MuiDialog-paper': { height: '90vh' } }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {activeTab === 0 ? <Satellite /> : <Streetview />}
          <Typography variant="h6">{title}</Typography>
        </Box>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 0, height: '100%' }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={activeTab} onChange={handleTabChange} aria-label="view type tabs">
            <Tab 
              icon={<Satellite />} 
              label="Satellite View" 
              iconPosition="start"
              sx={{ minHeight: 48 }}
            />
            <Tab 
              icon={<Streetview />} 
              label="Street View" 
              iconPosition="start"
              sx={{ minHeight: 48 }}
            />
          </Tabs>
        </Box>

        <Box sx={{ height: 'calc(100% - 48px)', position: 'relative' }}>
          {/* Address only when Regrid not configured; address + property cards when configured */}
          <Box sx={{ p: 2, backgroundColor: 'grey.50', borderBottom: 1, borderColor: 'divider' }}>
            <Typography variant="body2">
              <strong>Address:</strong> {address}
            </Typography>
            {regridConfigured && (
              <Box sx={{ mt: 2 }}>
                {loading && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <CircularProgress size={16} />
                    <Typography variant="caption">Loading property data...</Typography>
                  </Box>
                )}
                {error && (
                  <Alert severity="warning" sx={{ mb: 2, py: 0 }}>
                    <Typography variant="caption">{error}</Typography>
                  </Alert>
                )}
                {propertyData && (
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6} md={3}>
                      <Card variant="outlined" sx={{ p: 1 }}>
                        <CardContent sx={{ p: 1, '&:last-child': { pb: 1 } }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                            <SquareFoot color="primary" sx={{ fontSize: 20 }} />
                            <Typography variant="body2" fontWeight="bold">Lot Size</Typography>
                          </Box>
                          <Typography variant="h6" color="primary.main">
                            {propertyData.lotSizeFormatted}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {propertyData.lotSizeSqFt.toLocaleString()} sq ft
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                    {propertyData.propertyType && (
                      <Grid item xs={12} sm={6} md={3}>
                        <Card variant="outlined" sx={{ p: 1 }}>
                          <CardContent sx={{ p: 1, '&:last-child': { pb: 1 } }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                              <Home color="primary" sx={{ fontSize: 20 }} />
                              <Typography variant="body2" fontWeight="bold">Property Type</Typography>
                            </Box>
                            <Chip
                              label={propertyData.propertyType}
                              size="small"
                              color="primary"
                              variant="outlined"
                            />
                          </CardContent>
                        </Card>
                      </Grid>
                    )}
                    {propertyData.yearBuilt && (
                      <Grid item xs={12} sm={6} md={3}>
                        <Card variant="outlined" sx={{ p: 1 }}>
                          <CardContent sx={{ p: 1, '&:last-child': { pb: 1 } }}>
                            <Typography variant="body2" fontWeight="bold" sx={{ mb: 1 }}>
                              Year Built
                            </Typography>
                            <Typography variant="h6" color="primary.main">
                              {propertyData.yearBuilt}
                            </Typography>
                          </CardContent>
                        </Card>
                      </Grid>
                    )}
                    {propertyData.buildingArea && (
                      <Grid item xs={12} sm={6} md={3}>
                        <Card variant="outlined" sx={{ p: 1 }}>
                          <CardContent sx={{ p: 1, '&:last-child': { pb: 1 } }}>
                            <Typography variant="body2" fontWeight="bold" sx={{ mb: 1 }}>
                              Building Area
                            </Typography>
                            <Typography variant="h6" color="primary.main">
                              {propertyData.buildingArea.toLocaleString()} sq ft
                            </Typography>
                          </CardContent>
                        </Card>
                      </Grid>
                    )}
                  </Grid>
                )}
              </Box>
            )}
          </Box>
          
          {/* Keep both views in DOM and show/hide by tab so switching tabs doesn't remount iframe or crash Street View cleanup */}
          <Box sx={{ height: 'calc(100% - 48px)', position: 'relative' }}>
            {/* Satellite: always mounted when dialog open so iframe never remounts on tab switch */}
            <Box
              sx={{
                width: '100%',
                height: '100%',
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                display: activeTab === 0 ? 'block' : 'none',
                visibility: activeTab === 0 ? 'visible' : 'hidden',
              }}
            >
              <iframe
                src={open ? `https://www.google.com/maps?q=${encodeURIComponent(address)}&t=k&z=20&output=embed` : ''}
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Satellite View"
              />
            </Box>
            {/* Street View: always mounted when dialog open so cleanup doesn't run on tab switch */}
            <Box
              sx={{
                width: '100%',
                height: '100%',
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                display: activeTab === 1 ? 'flex' : 'none',
                visibility: activeTab === 1 ? 'visible' : 'hidden',
                flexDirection: 'column',
              }}
            >
              {googleMapsReady && (
                <Box
                  ref={streetViewContainerRef}
                  sx={{
                    width: '100%',
                    flex: 1,
                    minHeight: 300,
                    backgroundColor: '#e0e0e0',
                  }}
                />
              )}
              {streetViewEmbedLoading && (
                <Box
                  sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: 'rgba(255,255,255,0.9)',
                    zIndex: 1,
                  }}
                >
                  <Box sx={{ textAlign: 'center' }}>
                    <CircularProgress size={40} sx={{ mb: 2 }} />
                    <Typography variant="body2" color="text.secondary">
                      Loading Street View…
                    </Typography>
                  </Box>
                </Box>
              )}
              {streetViewEmbedError && (
                <Alert severity="info" sx={{ m: 2, flexShrink: 0 }}>
                  {streetViewEmbedError}
                </Alert>
              )}
              {(!googleMapsReady && activeTab === 1) && (
                <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', p: 3 }}>
                  <Typography variant="body2" color="text.secondary">
                    Loading map…
                  </Typography>
                </Box>
              )}
              <Box sx={{ p: 2, borderTop: 1, borderColor: 'divider', flexShrink: 0 }}>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={openStreetViewInNewTab}
                  disabled={streetViewOpening}
                  startIcon={streetViewOpening ? <CircularProgress size={16} color="inherit" /> : <Streetview />}
                >
                  {streetViewOpening ? 'Locating…' : 'Open in new tab'}
                </Button>
              </Box>
            </Box>
          </Box>
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 2, borderTop: 1, borderColor: 'divider' }}>
        <Button onClick={onClose} variant="contained">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SatelliteViewDialog;
